import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Fade } from '@material-ui/core';
import { ErrorOutline } from '@material-ui/icons';
import { identity, some } from 'lodash/fp';
import { Grid, Snackbar } from '../../common/components';
import messages from '../messages';
import cdsConfig from '../../app/styles/cds.config';
import { OrganizationMemberRole } from '../../common/constants/graphql';

const StyledButton = styled.button`
  box-sizing: border-box;
  border-radius: 4px;
  text-align: center;
  font-weight: ${cdsConfig.fontWeights.medium};
  cursor: pointer;
`;

const S = {
  SubmitButton: styled(StyledButton)`
    color: ${cdsConfig.colors.white};
    background-color: ${({ theme }) => theme.palette.primary.main};
    min-width: 90px;
    height: 40px;
    font-size: 0.9375rem;

    &:disabled {
      opacity: 0.5;
    }

    :not(:first-child) {
      margin-left: 8px;
    }
  `,

  Button: styled(StyledButton)`
    color: ${({ theme }) => theme.palette.grey[600]};
    min-width: 90px;
    height: 40px;
    font-size: 0.9375rem;
  `,
  Message: styled.p`
    display: flex;
    align-items: center;
  `,
  ErrorOutline: styled(ErrorOutline)`
    margin-right: 0.5rem;
  `,
};

const FormActions = ({
  errors, values, onSave, orgAdmin,
}) => {
  const [shown, setShown] = useState(false);

  const timeoutId = useRef(null);

  const saveDraft = useCallback(() => onSave(values), [values, onSave]);
  const showSnackbar = useCallback(() => {
    if (some(identity, errors)) {
      setShown(true);

      timeoutId.current = setTimeout(() => setShown(false), 3000);
    }
  }, [errors]);

  useEffect(() => () => clearTimeout(timeoutId.current), []);

  // FIXME 추후에 isValid로 변경해야 함. 지금은 formik에서 value가 변경될 때 error, isValid 데이터가 불명확함
  const invalidValues = !values?.title || !values?.file || !values?.targets;
  const disabled = Object.keys(errors).length > 0 || invalidValues;

  return (
    <>
      <Grid container>
        <Grid container item xs={7} justify="flex-end">
          <S.Button type="button" onClick={saveDraft}>
            <FormattedMessage {...messages.formActionsButtonSave} />
          </S.Button>
          <S.SubmitButton
            type="submit"
            color="primary"
            variant="contained"
            onClick={showSnackbar}
            disabled={disabled}
            data-event-on="click"
            data-event-name="NewsletterCreated"
            data-event-properties={JSON.stringify({
              orgId: orgAdmin?.data?.org?.id,
              orgName: orgAdmin?.data?.org?.name,
              orgType: orgAdmin?.data?.org?.category,
              orgAddress: orgAdmin?.data?.org?.address,
              isAdmin: orgAdmin?.data?.me?.organizationRole === OrganizationMemberRole.Admin || orgAdmin?.data?.me?.organizationRole === OrganizationMemberRole.SuperAdmin,
              newsletterRecipients: values?.targets?.length,
            })}
          >
            <FormattedMessage {...messages.formActionsButtonSend} />
          </S.SubmitButton>
        </Grid>
      </Grid>
      <Snackbar
        open={shown}
        TransitionComponent={Fade}
        ContentProps={{
          'aria-describedby': 'error-message',
        }}
        message={(
          <S.Message id="error-message">
            <S.ErrorOutline />
            필수 항목을 모두 작성해주세요.
          </S.Message>
        )}
        error
      />
    </>
  );
};

FormActions.propTypes = {
  errors: PropTypes.shape({}).isRequired,
  values: PropTypes.shape({}).isRequired,
  onSave: PropTypes.func.isRequired,
  orgAdmin: PropTypes.shape({}).isRequired,
};

const mapStateToProps = state => ({
  orgAdmin: state.admin.adminRelationships,
});

export default connect(
  mapStateToProps,
)(FormActions);
